import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/app/PageBits";
import { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Calculator, TrendingUp, TrendingDown, Minus, Image as ImageIcon, Upload, X, Mail, LogIn, Trash2, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtINR as fmtCurrency } from "@/lib/api";

export default function Projects() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", regular_rate: "", self_managed_rate: "", location: "",
    company_name: "", address: "", gstin: "", phone: "", email: "", website: "", bank_details: "", upi_handle: "",
    enabled_category_ids: [], hostnames: [], expense_approval_threshold: 0 });
  const [newHost, setNewHost] = useState("");

  // SMTP + impersonation sub-dialog
  const [smtpOpen, setSmtpOpen] = useState(false);
  const [smtpFor, setSmtpFor] = useState(null);
  const [smtp, setSmtp] = useState({ smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_from_name: "", smtp_from_email: "", smtp_reply_to: "" });
  const [smtpTest, setSmtpTest] = useState("");

  const load = async () => {
    const [pr, cats] = await Promise.all([api.get("/projects"), api.get("/expense-categories").catch(()=>({data:[]}))]);
    setList(pr.data); setCategories(cats.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const body = { ...form, regular_rate: Number(form.regular_rate), self_managed_rate: Number(form.self_managed_rate) };
      if (editing) await api.patch(`/projects/${editing.id}`, body);
      else await api.post("/projects", body);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setEditing(null);
      setForm({ name: "", regular_rate: "", self_managed_rate: "", location: "",
        company_name: "", address: "", gstin: "", phone: "", email: "", website: "", bank_details: "", upi_handle: "",
        enabled_category_ids: [], hostnames: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleCat = (cid) => {
    setForm((f) => ({ ...f, enabled_category_ids: f.enabled_category_ids.includes(cid)
      ? f.enabled_category_ids.filter(x => x !== cid)
      : [...f.enabled_category_ids, cid] }));
  };

  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, regular_rate: p.regular_rate, self_managed_rate: p.self_managed_rate, location: p.location || "",
    company_name: p.company_name || "", address: p.address || "", gstin: p.gstin || "", phone: p.phone || "", email: p.email || "", website: p.website || "", bank_details: p.bank_details || "", upi_handle: p.upi_handle || "",
    enabled_category_ids: p.enabled_category_ids || [], hostnames: p.hostnames || [], expense_approval_threshold: p.expense_approval_threshold || 0 }); setOpen(true); };

  const addHost = () => {
    const h = (newHost || "").trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
    if (!h) return;
    if ((form.hostnames || []).includes(h)) { toast.info("Already added"); return; }
    setForm({ ...form, hostnames: [...(form.hostnames || []), h] });
    setNewHost("");
  };
  const removeHost = (h) => setForm({ ...form, hostnames: (form.hostnames || []).filter(x => x !== h) });

  const openSmtp = (p) => {
    setSmtpFor(p);
    setSmtp({
      smtp_host: p.smtp_host || "",
      smtp_port: p.smtp_port || 587,
      smtp_user: p.smtp_user || "",
      smtp_password: "", // never prefilled; blank means "no change"
      smtp_from_name: p.smtp_from_name || p.company_name || p.name || "",
      smtp_from_email: p.smtp_from_email || "",
      smtp_reply_to: p.smtp_reply_to || "",
    });
    setSmtpTest("");
    setSmtpOpen(true);
  };
  const saveSmtp = async () => {
    try {
      const payload = { ...smtp };
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.patch(`/projects/${smtpFor.id}/smtp`, payload);
      toast.success("Mailbox saved"); setSmtpOpen(false); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const sendTestSmtp = async () => {
    const to = smtpTest || "";
    if (!to) { toast.error("Enter a recipient address"); return; }
    try {
      await api.post(`/projects/${smtpFor.id}/smtp/test`, { to });
      toast.success(`Test email queued → ${to}`);
    } catch(e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const impersonate = async (p) => {
    try {
      const r = await api.post(`/super-admin/impersonate/${p.id}`);
      // Open the target domain's handoff URL in a new tab
      window.open(r.data.handoff_url, "_blank", "noopener");
      toast.success(`Opening ${r.data.target_host} …`);
    } catch(e) { toast.error(e?.response?.data?.detail || "Could not start impersonation"); }
  };
  const isAdmin = user?.role === "super_admin";
  // Full-cascade delete only on central super-admin host (never while impersonating a project)
  const canHardDelete = isAdmin && !user?.active_project_id;

  // ---------- destructive delete ----------
  const [delOpen, setDelOpen] = useState(false);
  const [delProj, setDelProj] = useState(null);
  const [delPreview, setDelPreview] = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const openDelete = async (p) => {
    setDelProj(p);
    setDelConfirm("");
    setDelPreview(null);
    setDelOpen(true);
    setDelLoading(true);
    try {
      const r = await api.get(`/projects/${p.id}/delete-preview`);
      setDelPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
      setDelOpen(false);
    } finally {
      setDelLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!delProj) return;
    if ((delConfirm || "").trim() !== delProj.name) {
      toast.error("Type the project name exactly to confirm");
      return;
    }
    setDelBusy(true);
    try {
      await api.delete(`/projects/${delProj.id}`, { data: { confirm_name: delConfirm.trim() } });
      toast.success(`Deleted ${delProj.name} and all linked data`);
      setDelOpen(false);
      setDelProj(null);
      setDelPreview(null);
      setDelConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDelBusy(false);
    }
  };

  // logo upload
  const uploadLogo = async (p, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/projects/${p.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const removeLogo = async (p) => {
    if (!window.confirm(`Remove logo for ${p.name}?`)) return;
    try { await api.delete(`/projects/${p.id}/logo`); toast.success("Logo removed"); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const logoUrl = (p) => p.logo_file_id ? `${api.defaults.baseURL}/projects/${p.id}/logo?v=${p.logo_updated_at || ''}` : null;

  // rate simulator
  const [simOpen, setSimOpen] = useState(false);
  const [simProj, setSimProj] = useState(null);
  const [simRates, setSimRates] = useState({ regular_rate: "", self_managed_rate: "" });
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);

  const openSim = (p) => {
    setSimProj(p);
    setSimRates({ regular_rate: p.regular_rate, self_managed_rate: p.self_managed_rate });
    setSimResult(null); setSimOpen(true);
  };
  const runSim = async () => {
    setSimBusy(true);
    try {
      const r = await api.post(`/projects/${simProj.id}/simulate`, {
        regular_rate: Number(simRates.regular_rate),
        self_managed_rate: Number(simRates.self_managed_rate),
      });
      setSimResult(r.data);
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSimBusy(false); }
  };
  const applyFromSim = async () => {
    try {
      await api.patch(`/projects/${simProj.id}`, {
        name: simProj.name, location: simProj.location || "",
        regular_rate: Number(simRates.regular_rate),
        self_managed_rate: Number(simRates.self_managed_rate),
      });
      toast.success("Rates updated · applies to next cycle only");
      setSimOpen(false); load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Projects & Rates"
        subtitle="Set per-project maintenance rates (INR per sqft per month) for Regular and Self-Managed plans. Rate changes apply to next cycle only."
        actions={isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if(!v){ setEditing(null); setForm({name:"",regular_rate:"",self_managed_rate:"",location:"",company_name:"",address:"",gstin:"",phone:"",email:"",website:"",bank_details:"",upi_handle:""});} }}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="fab-m"><Plus className="h-4 w-4 mr-2"/>New project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name (short)</Label><Input data-testid="project-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
                  <div><Label>Location</Label><Input data-testid="project-location" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})}/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Regular rate (₹/sqft/mo)</Label><Input data-testid="project-regular-rate" type="number" step="0.01" value={form.regular_rate} onChange={(e)=>setForm({...form,regular_rate:e.target.value})}/></div>
                  <div><Label>Self-Managed rate</Label><Input data-testid="project-sm-rate" type="number" step="0.01" value={form.self_managed_rate} onChange={(e)=>setForm({...form,self_managed_rate:e.target.value})}/></div>
                </div>

                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-heading mt-2 mb-3">Company details · appear on invoices, receipts and emails</h3>
                  <div className="grid gap-3">
                    <div><Label>Legal company name</Label><Input data-testid="project-company" value={form.company_name} onChange={(e)=>setForm({...form,company_name:e.target.value})} placeholder="e.g. Tapovan Farms Pvt Ltd"/></div>
                    <div><Label>Address</Label><Input data-testid="project-address" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>GSTIN</Label><Input data-testid="project-gstin" value={form.gstin} onChange={(e)=>setForm({...form,gstin:e.target.value.toUpperCase()})}/></div>
                      <div><Label>Website</Label><Input data-testid="project-website" value={form.website} onChange={(e)=>setForm({...form,website:e.target.value})} placeholder="https://…"/></div>
                      <div><Label>Phone</Label><Input data-testid="project-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/></div>
                      <div><Label>Email</Label><Input data-testid="project-email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/></div>
                      <div><Label>UPI handle</Label><Input data-testid="project-upi" value={form.upi_handle} onChange={(e)=>setForm({...form,upi_handle:e.target.value})}/></div>
                      <div><Label>Bank details</Label><Input data-testid="project-bank" value={form.bank_details} onChange={(e)=>setForm({...form,bank_details:e.target.value})}/></div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-heading mt-2 mb-3">Vendor bills approval</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Approval threshold (₹)</Label>
                      <Input data-testid="project-approval-threshold" type="number" step="1" value={form.expense_approval_threshold}
                        onChange={(e)=>setForm({...form, expense_approval_threshold: Number(e.target.value || 0)})} placeholder="0 to disable"/>
                      <div className="text-xs text-muted-foreground mt-1">Bills ≥ this amount require a second admin's approval before becoming payable. 0 disables the approval step. Super admin bypasses.</div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-heading mt-2 mb-3">Hostnames · domains this project answers to</h3>
                  <div className="flex gap-2 mb-2">
                    <Input data-testid="host-input" placeholder="app.tapovanfarms.com" value={newHost} onChange={(e)=>setNewHost(e.target.value)}
                      onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); addHost(); }}}/>
                    <Button type="button" variant="outline" onClick={addHost} data-testid="host-add">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(form.hostnames || []).map(h => (
                      <span key={h} data-testid={`host-chip-${h}`} className="inline-flex items-center gap-2 bg-muted px-2.5 py-1 rounded text-xs">
                        {h}
                        <button type="button" onClick={()=>removeHost(h)} className="opacity-60 hover:opacity-100" title="Remove">×</button>
                      </span>
                    ))}
                    {!(form.hostnames || []).length && <span className="text-xs text-muted-foreground">No hostnames yet. Reserved: app.sanjeevaniestates.com</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">Point your domain's DNS at this app and link it in Emergent's Deploy panel. Then add the hostname here. The first hostname is the primary (shown in impersonation links and share URLs).</div>
                </div>

                <div className="pt-2 border-t border-border">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-heading mt-2 mb-3">Expense categories enabled for this project</h3>
                  <div className="border border-border rounded-md p-3 space-y-1.5 max-h-48 overflow-auto">
                    {categories.filter(c => !c.archived).map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox data-testid={`project-cat-${c.id}`}
                          checked={form.enabled_category_ids.includes(c.id)}
                          onCheckedChange={() => toggleCat(c.id)} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                    {categories.filter(c=>!c.archived).length === 0 && <div className="text-xs text-muted-foreground">No categories yet. Create some in the global master list.</div>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1.5">Only ticked categories appear in this project's expense dropdown.</div>
                </div>
              </div>
              <DialogFooter><Button data-testid="project-save-btn" onClick={submit}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider w-16">Logo</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Location</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Regular</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Self-Managed</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-border hover:bg-muted/40 transition-colors">
                <td className="px-4 py-3">
                  {logoUrl(p) ? (
                    <img src={logoUrl(p)} alt={`${p.name} logo`} data-testid={`project-logo-${p.id}`}
                      className="h-10 w-10 rounded object-contain border border-border bg-white"/>
                  ) : (
                    <div className="h-10 w-10 rounded border border-dashed border-border flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="h-4 w-4"/>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.location || "-"}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(p.regular_rate)}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(p.self_managed_rate)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1 items-center">
                    {isAdmin && (
                      <label className="inline-flex items-center gap-1 text-xs cursor-pointer px-2 py-1 rounded border border-border hover:bg-muted"
                        title={p.logo_file_id ? "Replace logo" : "Upload logo"}
                        data-testid={`upload-logo-${p.id}`}>
                        <Upload className="h-3.5 w-3.5"/>
                        <span>{p.logo_file_id ? "Replace" : "Logo"}</span>
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                          onChange={(e)=>{ uploadLogo(p, e.target.files); e.target.value=""; }}/>
                      </label>
                    )}
                    {isAdmin && p.logo_file_id && (
                      <Button data-testid={`remove-logo-${p.id}`} size="sm" variant="ghost" onClick={()=>removeLogo(p)} title="Remove logo">
                        <X className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {isAdmin && <Button data-testid={`sim-project-${p.id}`} size="sm" variant="ghost" onClick={()=>openSim(p)} title="Simulate rate change"><Calculator className="h-3.5 w-3.5"/></Button>}
                    {isAdmin && <Button data-testid={`smtp-${p.id}`} size="sm" variant="ghost" onClick={()=>openSmtp(p)} title={p.smtp_configured ? "Mailbox configured — edit" : "Configure project mailbox"}>
                      <Mail className={`h-3.5 w-3.5 ${p.smtp_configured ? "text-green-700" : ""}`}/>
                    </Button>}
                    {isAdmin && (p.hostnames || []).length > 0 && (
                      <Button data-testid={`enter-${p.id}`} size="sm" variant="outline" onClick={()=>impersonate(p)} title={`Open ${p.hostnames[0]} as super admin`}>
                        <LogIn className="h-3.5 w-3.5 mr-1"/>Enter
                      </Button>
                    )}
                    {isAdmin && <Button data-testid={`edit-project-${p.id}`} size="sm" variant="outline" onClick={()=>openEdit(p)}>Edit</Button>}
                    {canHardDelete && (
                      <Button data-testid={`delete-project-${p.id}`} size="sm" variant="ghost"
                        className="text-red-700 hover:text-red-800 hover:bg-red-50"
                        onClick={()=>openDelete(p)} title={`Delete ${p.name} and all linked data`}>
                        <Trash2 className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No projects yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Rate simulator · {simProj?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2 mb-4">Preview the impact on the next billing cycle for every active plot. Applying updates rates for future invoices only — existing invoices are never mutated.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-4 border-b border-border">
            <div>
              <Label>Regular rate (₹/sqft/mo)</Label>
              <Input data-testid="sim-regular" type="number" step="0.01" value={simRates.regular_rate}
                onChange={(e)=>setSimRates({...simRates, regular_rate: e.target.value})}/>
              <div className="text-xs text-muted-foreground mt-1">Current: {fmtCurrency(simProj?.regular_rate || 0)}</div>
            </div>
            <div>
              <Label>Self-Managed rate</Label>
              <Input data-testid="sim-selfmanaged" type="number" step="0.01" value={simRates.self_managed_rate}
                onChange={(e)=>setSimRates({...simRates, self_managed_rate: e.target.value})}/>
              <div className="text-xs text-muted-foreground mt-1">Current: {fmtCurrency(simProj?.self_managed_rate || 0)}</div>
            </div>
            <div className="flex items-end"><Button data-testid="sim-run" onClick={runSim} disabled={simBusy} className="w-full">Simulate</Button></div>
          </div>
          {simResult && (
            <div className="mt-5">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="border border-border rounded-md p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Current next cycle</div><div className="font-heading text-lg font-bold tabular">{fmtCurrency(simResult.totals.old)}</div></div>
                <div className="border border-border rounded-md p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Proposed next cycle</div><div className="font-heading text-lg font-bold tabular">{fmtCurrency(simResult.totals.new)}</div></div>
                <div className={`border rounded-md p-3 ${simResult.totals.delta > 0 ? "border-green-300 bg-green-50" : simResult.totals.delta < 0 ? "border-red-300 bg-red-50" : "border-border"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Delta</div>
                  <div className="font-heading text-lg font-bold tabular flex items-center gap-1">
                    {simResult.totals.delta > 0 ? <TrendingUp className="h-4 w-4 text-green-700"/> : simResult.totals.delta < 0 ? <TrendingDown className="h-4 w-4 text-red-700"/> : <Minus className="h-4 w-4"/>}
                    {fmtCurrency(Math.abs(simResult.totals.delta))}
                  </div>
                </div>
              </div>
              <div className="max-h-72 overflow-auto border border-border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 sticky top-0"><tr className="text-left">
                    <th className="px-3 py-2 font-heading uppercase tracking-wider">Plot</th>
                    <th className="px-3 py-2 font-heading uppercase tracking-wider">Owner</th>
                    <th className="px-3 py-2 font-heading uppercase tracking-wider">Plan · Cycle</th>
                    <th className="px-3 py-2 font-heading uppercase tracking-wider text-right">Old</th>
                    <th className="px-3 py-2 font-heading uppercase tracking-wider text-right">New</th>
                    <th className="px-3 py-2 font-heading uppercase tracking-wider text-right">Δ</th>
                  </tr></thead>
                  <tbody>
                    {simResult.rows.map((r) => (
                      <tr key={r.plot_id} data-testid={`sim-row-${r.plot_id}`} className="border-t border-border">
                        <td className="px-3 py-2 font-medium">{r.plot_no}</td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[140px]">{r.customer_name}</td>
                        <td className="px-3 py-2 capitalize">{r.plan.replace("_"," ")} · {r.billing_cycle.replace("_","-")}</td>
                        <td className="px-3 py-2 text-right tabular">{fmtCurrency(r.old_amount)}</td>
                        <td className="px-3 py-2 text-right tabular">{fmtCurrency(r.new_amount)}</td>
                        <td className={`px-3 py-2 text-right tabular ${r.delta > 0 ? "text-green-700" : r.delta < 0 ? "text-red-700" : "text-muted-foreground"}`}>{r.delta > 0 ? "+" : ""}{fmtCurrency(r.delta)}</td>
                      </tr>
                    ))}
                    {simResult.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No active plots to simulate.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={()=>setSimOpen(false)}>Cancel</Button>
            {simResult && <Button data-testid="sim-apply" onClick={applyFromSim}>Apply new rates</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={smtpOpen} onOpenChange={setSmtpOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Project mailbox · {smtpFor?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">
              This mailbox sends invoices, receipts and reminders on behalf of <b>{smtpFor?.company_name || smtpFor?.name}</b>. For Gmail / Google Workspace use an App Password.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SMTP host</Label><Input data-testid="smtp-host" value={smtp.smtp_host} onChange={(e)=>setSmtp({...smtp,smtp_host:e.target.value})} placeholder="smtp.gmail.com"/></div>
              <div><Label>Port</Label><Input data-testid="smtp-port" type="number" value={smtp.smtp_port} onChange={(e)=>setSmtp({...smtp,smtp_port:Number(e.target.value)})}/></div>
              <div><Label>Username / email</Label><Input data-testid="smtp-user" value={smtp.smtp_user} onChange={(e)=>setSmtp({...smtp,smtp_user:e.target.value})}/></div>
              <div><Label>App password <span className="text-muted-foreground">(leave blank to keep)</span></Label><Input data-testid="smtp-password" type="password" value={smtp.smtp_password} onChange={(e)=>setSmtp({...smtp,smtp_password:e.target.value})}/></div>
              <div><Label>From name</Label><Input data-testid="smtp-from-name" value={smtp.smtp_from_name} onChange={(e)=>setSmtp({...smtp,smtp_from_name:e.target.value})}/></div>
              <div><Label>From email</Label><Input data-testid="smtp-from-email" value={smtp.smtp_from_email} onChange={(e)=>setSmtp({...smtp,smtp_from_email:e.target.value})}/></div>
              <div className="col-span-2"><Label>Reply-to (optional)</Label><Input data-testid="smtp-reply-to" value={smtp.smtp_reply_to} onChange={(e)=>setSmtp({...smtp,smtp_reply_to:e.target.value})}/></div>
            </div>
            <div className="pt-2 border-t border-border">
              <Label>Send a test email to</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="smtp-test-to" placeholder="you@example.com" value={smtpTest} onChange={(e)=>setSmtpTest(e.target.value)}/>
                <Button type="button" variant="outline" data-testid="smtp-test-send" onClick={sendTestSmtp}>Send test</Button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">The mailbox must be saved first for the test to use the latest values.</div>
            </div>
          </div>
          <DialogFooter><Button data-testid="smtp-save-btn" onClick={saveSmtp}>Save mailbox</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- destructive delete ---------- */}
      <Dialog open={delOpen} onOpenChange={(v)=>{ if(!delBusy) setDelOpen(v); }}>
        <DialogContent className="max-w-xl" data-testid="delete-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5"/> Delete project · {delProj?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900">
              <div className="font-semibold mb-1">This is irreversible.</div>
              <div className="text-xs">Every plot, invoice, payment, vendor, expense, note, attachment and logo tied to this project will be permanently deleted. Admins assigned only to this project will lose their access.</div>
            </div>

            {delLoading && <div className="text-muted-foreground" data-testid="delete-preview-loading">Loading impact preview…</div>}

            {delPreview && (
              <div className="border border-border rounded-md divide-y divide-border" data-testid="delete-preview-counts">
                {[
                  ["Plots", delPreview.counts.plots],
                  ["Invoices", delPreview.counts.invoices],
                  ["Payments", delPreview.counts.payments],
                  ["Vendors", delPreview.counts.vendors],
                  ["Expenses", delPreview.counts.expenses],
                  ["Expense payments", delPreview.counts.expense_payments],
                  ["Plan-change requests", delPreview.counts.plan_change_requests],
                  ["Pause requests", delPreview.counts.pause_requests],
                  ["Plot transfers", delPreview.counts.plot_transfers],
                  ["Plot notes", delPreview.counts.plot_notes],
                  ["Note attachments", delPreview.counts.note_attachments],
                  ["Expense attachments", delPreview.counts.expense_attachments],
                  ["Admins unlinked", delPreview.counts.admins_linked],
                  ["Logo file", delPreview.counts.has_logo ? 1 : 0],
                ].map(([label, n]) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2 text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span className={`font-medium tabular ${Number(n) > 0 ? "text-red-800" : "text-muted-foreground"}`}
                      data-testid={`delete-count-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>{n}</span>
                  </div>
                ))}
                {(delPreview.project.hostnames || []).length > 0 && (
                  <div className="px-3 py-2 text-xs">
                    <div className="text-muted-foreground mb-1">Hostnames freed</div>
                    <div className="flex flex-wrap gap-1">
                      {delPreview.project.hostnames.map(h => (
                        <span key={h} className="bg-muted px-2 py-0.5 rounded text-[11px]">{h}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="confirm-name" className="text-xs">
                Type <span className="font-mono font-semibold text-red-800">{delProj?.name}</span> to confirm
              </Label>
              <Input
                id="confirm-name"
                data-testid="delete-confirm-input"
                value={delConfirm}
                onChange={(e)=>setDelConfirm(e.target.value)}
                placeholder={delProj?.name || ""}
                autoComplete="off"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setDelOpen(false)} disabled={delBusy}>Cancel</Button>
            <Button
              data-testid="delete-project-confirm-btn"
              className="bg-red-700 hover:bg-red-800 text-white"
              disabled={delBusy || delLoading || (delConfirm.trim() !== (delProj?.name || "__"))}
              onClick={confirmDelete}
            >
              {delBusy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
